import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { Stack, Typography } from '@mui/material'

// `Input`, not `@repo/ui`'s `TextField`. `TextField` is a styled MUI TextField and
// would render MUI's own floating label; `Input` renders the house `FieldLabel`,
// which owns the canonical 8px label-to-control gap (§31). Same reason
// `RenameRoleModal` and `SignupDomainModal` both use it.
import { Modal, Button, Input } from '@repo/ui'

import { FormSubmitButton } from 'components/input'
import { useSnackbar } from 'contexts'
import { useSendInstallInstructions } from 'hooks/api'
import { getErrorMessage } from 'utils'

interface SendInstallInstructionsModalProps {
  open: boolean
  onClose: () => void
}

interface FormValues {
  to: string
}

/**
 * VRG-560 — "Send install instructions".
 *
 * Emails the brand's rendered widget snippet, with steps, to a typed address.
 *
 * ## Why the field is a free-typed address and not a user picker
 *
 * The person who installs the snippet is usually NOT a Vera user. They are the
 * brand's web developer, or an agency, or a contractor — someone who will never
 * have an account here and does not need one to do this job. A picker over known
 * users would therefore fail in exactly the case the feature exists for, which is
 * why the endpoint accepts any valid address by design rather than by omission.
 *
 * The field starts EMPTY on purpose and is not prefilled with the signed-in
 * user's address, unlike the Labs send-test box it otherwise resembles. Here the
 * recipient is by definition someone other than you, so a prefill would be wrong
 * more often than right — and a populated-but-pristine form would also leave Send
 * disabled with no visible reason why.
 */
export const SendInstallInstructionsModal = ({
  open,
  onClose,
}: SendInstallInstructionsModalProps) => {
  const { open: notification } = useSnackbar()

  const { control, handleSubmit, reset } = useForm<FormValues>({
    // `onChange` so the address is validated as it is typed, which is what makes
    // `FormSubmitButton`'s error half do any work — with the default `onSubmit`
    // mode `errors` stays empty until a submit attempt, so the button would sit
    // enabled over an address already known to be malformed.
    mode: 'onChange',
    defaultValues: { to: '' },
  })

  // Clear the previous recipient whenever the modal reopens. Without this the
  // last address typed is still sitting there — and worse, still DIRTY, so Send
  // is live and one stray click re-sends to whoever it was last time.
  useEffect(() => {
    if (open) reset({ to: '' })
  }, [open, reset])

  const { mutate: sendInstructions, isPending } = useSendInstallInstructions()

  const handleClose = () => {
    // Not gated on `isPending`: Cancel is the way out and stays available (§54).
    // The send is already in flight server-side, so closing cannot cancel it —
    // it only stops the user being held on a modal waiting for a toast.
    onClose()
  }

  const onSubmit = handleSubmit(({ to }) => {
    sendInstructions(
      { to },
      {
        onSuccess: () => {
          notification({
            type: 'success',
            title: 'Install instructions sent',
            // The address is echoed back because it was typed by hand and a typo
            // in it is the single most likely thing to go wrong here. Seeing it
            // is what lets someone catch a wrong address while they still
            // remember what they meant to type.
            text: `Sent to ${to}.`,
          })
          onClose()
        },
        onError: (error: unknown) => {
          notification({ type: 'error', title: getErrorMessage(error) })
        },
      },
    )
  })

  return (
    <Modal
      title="Send install instructions"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <FormSubmitButton
            control={control}
            type="submit"
            form="send-install-instructions-form"
            loading={isPending}
            disabled={isPending}
          >
            Send
          </FormSubmitButton>
        </>
      }
    >
      <Stack component="form" id="send-install-instructions-form" onSubmit={onSubmit} spacing={4}>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          We will email the code snippet and the steps for adding it. The recipient does not need
          a Vera account.
        </Typography>

        <Controller
          name="to"
          control={control}
          rules={{
            required: 'Email is required',
            pattern: {
              value: /\S+@\S+\.\S+/,
              message: 'Enter a valid email address',
            },
          }}
          render={({ field, fieldState }) => (
            // Mapped field-by-field rather than spread: `Input.onChange` emits a
            // STRING, not a change event, so `{...field}` would hand RHF a bare
            // string where it expects an event. RHF's own `onChange` accepts
            // either, so passing it straight through is correct.
            <Input
              label="Send to"
              type="email"
              placeholder="developer@example.com"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              inputRef={field.ref}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Stack>
    </Modal>
  )
}
